import React, { useCallback, useEffect, useRef, useState } from 'react';
import useFetch from '../../hooks/useFetch';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { setLocalStorage } from '../../utils/func';
import { abortable } from '../../utils/abortable';
import { rememberOAuthConsent, consumeOAuthConsent, consumeOAuthAttempt, clearOAuthCallback, redirectToOAuth } from './oauthConsent';
import { isNativeGoogleAuth, createGoogleProof, googleAuthorizationUrl, readGoogleCallback, nativeGoogleReturn,
  openNativeGoogleAuth, cancelNativeGoogleAuth } from './googleOAuth';
import './AuthButtonGoogleCustom.scss';
import { socialRequest } from '../../socialAuth/client';
import { loginPurpose } from '../../accountDeletion/loginPurpose';

export default function AuthButtonGoogleCustom({ disabled = true, onBusy }) {
  const { handleLogin, loading, error } = useFetch('google');
  const { t } = useTranslation();
  const location = useLocation();
  const [authError, setAuthError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const pending = useRef(false);
  const processedCallback = useRef('');
  const preparation = useRef(null);

  const [returnToApp, setReturnToApp] = useState('');

  useEffect(() => {
    const restore = (event) => {
      if (!event.persisted || isNativeGoogleAuth()) return;
      preparation.current?.abort();
      consumeOAuthConsent('google', pending.current);
      pending.current = false;
      setRedirecting(false);
    };
    window.addEventListener('pageshow', restore);
    return () => {
      window.removeEventListener('pageshow', restore);
      preparation.current?.abort();
      if (typeof pending.current === 'string' && isNativeGoogleAuth()) {
        consumeOAuthConsent('google', pending.current);
        cancelNativeGoogleAuth(pending.current);
      }
      pending.current = false;
    };
  }, []);

  const complete = useCallback((address, cleanAddress = false) => {
    const callback = readGoogleCallback(address);
    if (!callback) return;
    if (cleanAddress) clearOAuthCallback(['code', 'id_token', 'error', 'error_description', 'state', 'scope', 'authuser', 'prompt']);
    const key = JSON.stringify(callback);
    if (processedCallback.current === key) return;
    processedCallback.current = key;
    const relay = !isNativeGoogleAuth() && nativeGoogleReturn(callback);
    if (relay) {
      setReturnToApp(relay);
      // Browsers may require another user gesture; keep a visible fallback.
      try { redirectToOAuth(relay); } catch { /* the return button remains usable */ }
      return;
    }
    const attempt = consumeOAuthAttempt('google', callback.state);
    if (!attempt || callback.invalid) {
      setAuthError('brand:authError');
      return;
    }
    pending.current = false;
    setRedirecting(false);
    if (isNativeGoogleAuth()) cancelNativeGoogleAuth(callback.state);
    if (callback.error || (callback.code && !attempt.verifier) || (callback.id_token && attempt.verifier) || (callback.code && callback.id_token)) {
      setAuthError('brand:authError');
      return;
    }
    const payload = callback.code
      ? { code: callback.code, code_verifier: attempt.verifier, nonce: callback.state }
      : { credential: callback.id_token };
    Promise.resolve(handleLogin({ ...payload, registration: attempt.registration })).catch(() => setAuthError('brand:authError'));
  }, [handleLogin]);

  useEffect(() => { complete(window.location.href, true); }, [location, complete]);

  const busy = loading || redirecting;
  useEffect(() => { onBusy?.(busy ? 'google' : null); }, [busy, onBusy]);

  const signIn = async () => {
    if (disabled || loading || pending.current) return;
    setAuthError('');
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) { setAuthError('brand:authUnavailable'); return; }
    const preparing = Symbol('preparing');
    pending.current = preparing;
    setRedirecting(true);
    const controller = new AbortController();
    preparation.current = controller;
    const timer = setTimeout(() => controller.abort(), 15000);
    let state;
    try {
      const proof = await abortable(createGoogleProof(), controller.signal);
      if (pending.current !== preparing) return;
      const started = await abortable(socialRequest('/primary/google/start', { challenge: proof.challenge,
        platform: isNativeGoogleAuth() ? 'ios' : 'web', consent: true,
        purpose: loginPurpose(),
      }, controller.signal), controller.signal);
      if (pending.current !== preparing) return;
      if (!/^(?:ios\.)?[a-f0-9]{64}$/.test(started.state || '')) throw Error('Invalid auth attempt');
      state = rememberOAuthConsent('google', { verifier: proof.verifier, native: isNativeGoogleAuth(), serverState: started.state });
      if (!state) throw new Error('Unavailable');
      const url = googleAuthorizationUrl({ clientId, state, challenge: proof.challenge });
      const from = new URLSearchParams(window.location.search).get('from') || '/';
      setLocalStorage('login_from', from);
      pending.current = state;
      // The deadline covers bridge preparation, not the user's interaction
      // with Google's sign-in sheet, which can legitimately take longer.
      clearTimeout(timer);
      preparation.current = null;
      if (isNativeGoogleAuth()) {
        const result = await openNativeGoogleAuth(url, state);
        if (pending.current !== state) return; // HTTPS App Link may have already completed this attempt.
        if (result.cancelled) {
          consumeOAuthConsent('google', state);
          pending.current = false;
          setRedirecting(false);
        } else {
          const callback = readGoogleCallback(result.url);
          if (!callback || callback.invalid || callback.state !== state) throw new Error('Invalid callback');
          complete(result.url);
        }
      } else {
        redirectToOAuth(url);
      }
    } catch {
      if (pending.current !== (state || preparing)) return;
      if (state) consumeOAuthConsent('google', state);
      pending.current = false;
      setRedirecting(false);
      setAuthError('brand:authError');
    } finally { clearTimeout(timer); if (preparation.current === controller) preparation.current = null; }
  };

  if (returnToApp) return <button type="button" className="custom-google-button button_border"
    onClick={() => redirectToOAuth(returnToApp)}>{t('brand:authReturnToApp')}</button>;


  return (
    <div className="google-button-wrapper">
      {(error || authError) && <p className="error-message" role="alert">{authError ? t(authError) : t('brand:authError')}</p>}
      <button type="button" className={`custom-google-button button_border ${busy ? 'auth-button-busy' : ''}`}
        disabled={disabled || busy} onClick={signIn} aria-busy={busy}>
        <svg aria-hidden="true" focusable="false" className="google-icon" viewBox="0 0 24 24" width="20" height="20">
						<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
						<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
						<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
						<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
					</svg>
        <span className="button-text">{t('login.button_google')}</span>
      </button>
    </div>
  );
}
